// ---------- START IMPORT BLOCK ----------
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import { useLanguage } from "../../../API/Context/LanguageContext";
import { useUser } from "../../../API/Context/UserContext";
import VocabQualifications from "../../../ConfigFiles/multilingual/VocabQualifications";
import { VocabButtons as btn_vocab } from "../../../ConfigFiles/multilingual/VocabButtons";
import { ExclamationTriangle } from "react-bootstrap-icons";
// ---------- END IMPORT BLOCK ----------

const CATEGORY_MAP = [
  { value: 0, vocabKey: 'profesional' },
  { value: 1, vocabKey: 'masters' },
  { value: 2, vocabKey: 'doctorate' },
];

const REQUIRED_FIELDS = ['title', 'institution', 'year_of_completion'];

const validateQualifications = (qualifications = []) => {
  let isOverallValid = true;
  let isLastComplete = true; 
  const detailedErrors = {};

  const activeQualifications = qualifications
    .map((q, originalIndex) => ({ ...q, originalIndex }))
    .filter(q => !q.deleted);

  activeQualifications.forEach((q, activeIdx) => {
    const errors = {};
    let qualificationIsValid = true;

    REQUIRED_FIELDS.forEach(field => {
      const value = q[field];
      if (!value || String(value).trim() === '') {
        errors[field] = true;
        qualificationIsValid = false;
        isOverallValid = false;
      }
    });
    
    const yearValue = String(q.year_of_completion || ''); 
    if (q.year_of_completion && (yearValue.length !== 4 || isNaN(Number(yearValue)))) {
      errors.year_of_completion = true;
      qualificationIsValid = false;
      isOverallValid = false;
    }

    detailedErrors[q.originalIndex] = errors;

    if (activeIdx === activeQualifications.length - 1 && !qualificationIsValid) {
      isLastComplete = false;
    }
  });

  return { isOverallValid, detailedErrors, isLastComplete };
};

const QualificationForm = forwardRef(({ onChange }, ref) => {
  const { lan } = useLanguage();
  const { user } = useUser();

  const mapApiQualificationsToForm = (rawQualifications = []) => {
    if (!Array.isArray(rawQualifications)) return [];
    return rawQualifications.map((q) => ({
      id: q.id || null,
      deleted: q.deleted || false, 
      title: q.title || "",
      institution: q.institution || "",
      category: q.category ?? 0, 
      year_of_completion: String(q.year_of_completion || q.year || ""),
      license: q.license || q.credential_id || "",
    }));
  };

  const [qualifications, setQualifications] = useState(() => 
    mapApiQualificationsToForm(user?.professional?.qualifications)
  );

  const [validation, setValidation] = useState(() => validateQualifications(qualifications));
  const lastSubmittedDataRef = useRef(null);

  useEffect(() => {
    if (user?.professional?.qualifications) {
      setQualifications(mapApiQualificationsToForm(user.professional.qualifications));
    }
  }, [user]);

  const formatDataForApi = (currentQualifications = []) => {
    return currentQualifications.map(q => ({
      title: q.title,
      institution: q.institution,
      category: Number(q.category),
      year: Number(q.year_of_completion) || null,
      credential_id: q.license,
      ...(q.id && { id: q.id }),
      ...(q.deleted && { deleted: true })
    }));
  };

  useEffect(() => {
    const validationResult = validateQualifications(qualifications);
    setValidation(validationResult);

    const dataForApi = formatDataForApi(qualifications);
    const newData = validationResult.isOverallValid ? dataForApi : null;
    
    if (JSON.stringify(newData) !== JSON.stringify(lastSubmittedDataRef.current)) {
      lastSubmittedDataRef.current = newData;
      onChange?.(newData);
    }
  }, [qualifications, onChange]);

  useImperativeHandle(ref, () => ({
    getData: () => {
      const dataForApi = formatDataForApi(qualifications);
      const validationResult = validateQualifications(qualifications);
      return validationResult.isOverallValid ? dataForApi : null;
    },
  }));
  
  const handleChange = (index, field, value) => {
    if (field === 'year_of_completion') {
      value = String(value).replace(/\D/g, '').substring(0, 4);
    }

    setQualifications((prev) =>
      prev.map((q, i) => (i === index ? { ...q, [field]: value } : q))
    );
  };
  
  const addQualification = () =>
    setQualifications((prev) => [
      ...prev,
      { id: null, deleted: false, title: "", institution: "", category: 0, year_of_completion: "", license: "" },
    ]);

  const removeQualification = (index) => {
    const q = qualifications[index];
    if (q?.id) {
      setQualifications((prev) =>
        prev.map((item, i) => i === index ? { ...item, deleted: true } : item)
      );
    } else {
      setQualifications((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const errorStyle = { borderColor: 'red', borderWidth: '2px' };

  return (
    <div>
      {qualifications
        .map((q, originalIndex) => ({ ...q, originalIndex }))
        .filter(q => !q.deleted)
        .map((q) => {
          const originalIndex = q.originalIndex;
          const errors = validation?.detailedErrors?.[originalIndex] ?? {};
          
          return (
            <div key={originalIndex} className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-end mb-2">
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={() => removeQualification(originalIndex)}
                >
                  {btn_vocab.remove[lan]}
                </Button>
              </div>
              
              <Row className="mb-2">
                <Col>
                  <Form.Label>{VocabQualifications.title[lan]}</Form.Label>
                  <Form.Control
                    placeholder={VocabQualifications.title[lan]}
                    value={q.title}
                    onChange={(e) => handleChange(originalIndex, "title", e.target.value)}
                    style={errors.title ? errorStyle : {}}
                  />
                </Col>
                <Col>
                  <Form.Label>{VocabQualifications.institution[lan]}</Form.Label>
                  <Form.Control
                    placeholder={VocabQualifications.institution[lan]}
                    value={q.institution}
                    onChange={(e) => handleChange(originalIndex, "institution", e.target.value)}
                    style={errors.institution ? errorStyle : {}}
                  />
                </Col>
              </Row>

              <Row className="mb-2">
                <Col>
                  <Form.Label>{VocabQualifications.category[lan]}</Form.Label>
                  <Form.Select
                    value={q.category}
                    onChange={(e) => handleChange(originalIndex, "category", e.target.value)}
                  >
                    {CATEGORY_MAP.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {VocabQualifications[cat.vocabKey][lan]}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label>{VocabQualifications.year_of_completion[lan]}</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="YYYY"
                    value={q.year_of_completion}
                    onChange={(e) => handleChange(originalIndex, "year_of_completion", e.target.value)}
                    style={errors.year_of_completion ? errorStyle : {}}
                    maxLength={4}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label>{VocabQualifications.license[lan]}</Form.Label>
                  <Form.Control
                    placeholder={VocabQualifications.license[lan]}
                    value={q.license}
                    onChange={(e) => handleChange(originalIndex, "license", e.target.value)}
                  />
                </Col>
              </Row>
            </div>
          );
        })}
      
      <Button 
        onClick={addQualification}
        disabled={!validation?.isOverallValid || !validation?.isLastComplete}
      >
        {VocabQualifications.new_qualification[lan]}
      </Button>

      {validation && !validation.isOverallValid && (
        <div className="mt-3 p-2 border border-danger text-danger rounded">
          <ExclamationTriangle className="me-2" />
          <strong>Error:</strong>
          {!validation.isLastComplete && (
            <p className="m-0 mt-1">{VocabQualifications.missing_fields[lan]}</p>
          )}
        </div>
      )}
    </div>
  );
});

export default QualificationForm;